/**
 * RaceDayCard — v16.3 §1.1 (Alberto): tarjeta del día de la carrera
 * renderizada en PlanPage entre el WeekView de la última semana del plan
 * activo y el resto de acciones.
 *
 * Estados visuales (decididos por race_date relativo a today):
 * - race_date > today: render "Faltan X días para tu carrera" + info
 *   (nombre + distancia + fecha formateada). SIN botón.
 * - race_date == today: render "¡Hoy es tu día!" + botón "Reportar resultado".
 * - race_date < today: render "Tu carrera fue el {date}" + botón
 *   "Reportar resultado" (el atleta puede registrar el resultado al
 *   día siguiente; bloque A del trigger automático lo transiciona a
 *   recovery el día siguiente).
 *
 * Botón "Reportar resultado" SOLO si conocemos `primaryRaceId` (el
 * RaceTarget que ancla el plan activo). PlanPage hace fetch separado de
 * `getRaces()` y resuelve el primary; si no existe, el botón no se
 * renderiza (atleta puede ir a /carreras y usar RaceCard "Añadir
 * resultado" del sub-bloque B.4).
 *
 * Cuando race ya está `completed=true`, mostramos badge "Resultado
 * registrado" en lugar del botón.
 */
import { useState } from "react";

import { RaceResultModal } from "@/components/races/RaceResultModal";
import { distanceLabel } from "@/components/races/distanceLabel";
import { t } from "@/i18n/es";
import type { RaceTarget, TrainingPlan } from "@/types/api";

interface Props {
  plan: TrainingPlan;
  /** RaceTarget `is_primary=true` del atleta (ancla el plan activo). Null
   *  si PlanPage no ha podido resolverlo (fetch fallido o atleta sin
   *  races). En ese caso solo se muestra info, no botón. */
  primaryRace: RaceTarget | null;
  /** Callback al éxito del modal: PlanPage refresca el plan + races. */
  onResultSaved: () => void;
}

function _formatDateLocal(iso: string): string {
  // T00:00:00 fuerza interpretación local (bug TZ S11.2, ya consolidado
  // en RaceCard y SessionDetailPage).
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("es-ES", {
    weekday: "long",
    day: "numeric",
    month: "short",
  });
}

/** Días entre two ISO dates (sin TZ, días enteros). Negativo si race
 *  pasada. Aritmética UTC pura (patrón consolidado del proyecto). */
function _daysBetween(fromIso: string, toIso: string): number {
  const from = new Date(`${fromIso}T00:00:00Z`);
  const to = new Date(`${toIso}T00:00:00Z`);
  return Math.round((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24));
}

function _todayIsoUtc(): string {
  const now = new Date();
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(
    2,
    "0",
  )}-${String(now.getUTCDate()).padStart(2, "0")}`;
}

export function RaceDayCard({ plan, primaryRace, onResultSaved }: Props) {
  const [modalOpen, setModalOpen] = useState(false);

  const today = _todayIsoUtc();
  const daysUntil = _daysBetween(today, plan.race_date);
  const dateLabel = _formatDateLocal(plan.race_date);
  const distanceText = distanceLabel(plan.race_distance);

  // Subtítulo según relación temporal.
  let subtitle: string;
  if (daysUntil > 0) {
    subtitle = t.plan.raceDayCardUpcoming.replace("{days}", String(daysUntil));
  } else if (daysUntil === 0) {
    subtitle = t.plan.raceDayCardToday;
  } else {
    subtitle = t.plan.raceDayCardPast.replace("{date}", dateLabel);
  }

  const canReportResult = daysUntil <= 0 && primaryRace !== null;
  const alreadyCompleted = primaryRace?.completed === true;

  return (
    <section
      data-testid="race-day-card"
      className="rounded-md border border-lime/40 bg-lime/5 p-4"
    >
      <p className="text-xs uppercase tracking-widest text-lime">
        {t.plan.raceDayCardTitle}
      </p>
      <h2 className="mt-2 text-base font-medium text-bone">
        {primaryRace?.name || distanceText}
      </h2>
      <p className="mt-1 text-xs text-bone-muted">
        {dateLabel} · {distanceText}
      </p>
      <p
        data-testid="race-day-card-subtitle"
        className="mt-2 text-sm text-bone"
      >
        {subtitle}
      </p>

      {alreadyCompleted && (
        <p
          data-testid="race-day-card-completed"
          className="mt-3 text-xs text-bone-muted"
        >
          {t.plan.raceDayCardCompletedNote}
        </p>
      )}

      {canReportResult && !alreadyCompleted && (
        <button
          type="button"
          data-testid="race-day-card-report-btn"
          onClick={() => setModalOpen(true)}
          className="mt-3 rounded-md bg-lime px-4 py-2 text-sm font-medium text-graphite transition-colors duration-fast hover:bg-lime/90"
        >
          {t.plan.raceDayCardCta}
        </button>
      )}

      {primaryRace !== null && (
        <RaceResultModal
          raceId={primaryRace.id}
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          onSuccess={() => {
            setModalOpen(false);
            onResultSaved();
          }}
        />
      )}
    </section>
  );
}
